import React from "react";

import { cssVariables } from "./css_variables";
import { stylex } from "./stylex";

export type TextSize = "xl" | "lg" | "md" | "sm" | "xs";
export type TextColor =
  | "white"
  | "default"
  | "muted"
  | "link"
  | "success"
  | "error"
  | "primary";
export type TextDecoration = "overline" | "lineThrough" | "underline";

export interface TextProps {
  children?: React.ReactNode;
  fontSize?: TextSize;
  color?: TextColor;
  align?: "left" | "right" | "center";
  isBold?: boolean;
  testID?: string;
  decoration?: TextDecoration;
  overflow?: "ellipsis";
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "span";
  uppercase?: boolean;
}

/**
 * Run of text with a single style.
 */
export function Text(props: TextProps) {
  const {
    align = "left",
    children,
    as,
    color = "default",
    fontSize = "md",
    decoration,
    testID,
    isBold,
    overflow,
    uppercase,
  } = props;

  const Tag = as ?? "span";

  return (
    <Tag
      data-component="Text"
      data-testid={testID}
      style={styles(
        "root",
        color,
        fontSize,
        align,
        isBold && "bold",
        decoration,
        overflow,
        uppercase && "uppercase"
      )}
    >
      {children}
    </Tag>
  );
}

const styles = stylex.create({
  root: {
    margin: 0,
    padding: 0,
    textShadow: "none",
    boxShadow: "none",
    fontWeight: "normal",
    fontFamily: cssVariables.fontFamilyBody,
  },
  xl: {
    fontSize: cssVariables.textSizeXL,
    lineHeight: cssVariables.textLineHeightXL,
  },
  lg: {
    fontSize: cssVariables.textSizeLG,
    lineHeight: cssVariables.textLineHeightLG,
  },
  md: {
    fontSize: cssVariables.textSizeMD,
    lineHeight: cssVariables.textLineHeightMD,
  },
  sm: {
    fontSize: cssVariables.textSizeSM,
    lineHeight: cssVariables.textLineHeightSM,
  },
  xs: {
    fontSize: cssVariables.textSizeXS,
    lineHeight: cssVariables.textLineHeightXS,
  },
  default: {
    color: cssVariables.textColorDefault,
  },
  muted: {
    color: cssVariables.textColorMuted,
  },
  success: {
    color: cssVariables.textColorSuccess,
  },
  error: {
    color: cssVariables.textColorError,
  },
  primary: {
    color: cssVariables.textColorPrimary,
  },
  link: {
    color: cssVariables.textColorLink,
  },
  white: {
    color: "white",
  },
  left: {
    textAlign: "left",
  },
  right: {
    textAlign: "right",
  },
  center: {
    textAlign: "center",
  },
  bold: {
    fontWeight: "bold",
  },
  overline: {
    textDecoration: "overline",
  },
  lineThrough: {
    textDecoration: "line-through",
  },
  underline: {
    textDecoration: "underline",
  },
  ellipsis: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  uppercase: {
    textTransform: "uppercase",
  },
});
